import { useState, useEffect } from 'react';
import { Search, Filter, X, Loader2 } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { useLang } from '../context/LangContext';

import { normalizeCmsImages, readSessionCache } from '../api/config';
import { extractListPayload, normalizeProducts } from '../utils/products';
import { cmsApi, productsApi } from '../api/requests';

const PRODUCT_CACHE_KEY = 'catalog-products-cache';
const CATEGORY_CACHE_KEY = 'catalog-categories-cache';

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [cms, setCms] = useState(null);
  const { t, lang } = useLang();

  useEffect(() => {
    const fetchData = async () => {
      const cachedProducts = readSessionCache(PRODUCT_CACHE_KEY);
      const cachedCategories = readSessionCache(CATEGORY_CACHE_KEY);

      if (cachedProducts) {
        setProducts(normalizeProducts(cachedProducts));
      }

      if (cachedCategories) {
        setCategories(extractListPayload(cachedCategories, 'categories'));
      }

      try {
        const [productsRes, categoriesRes, cmsRes] = await Promise.all([
          productsApi.getAll(),
          productsApi.getCategories(),
          cmsApi.getSection('collection')
        ]);
        const productsData = normalizeProducts(productsRes.data);
        const categoriesData = extractListPayload(categoriesRes.data, 'categories');

        setProducts(productsData);
        setCategories(categoriesData);
        setCms(normalizeCmsImages(cmsRes.data));
        sessionStorage.setItem(PRODUCT_CACHE_KEY, JSON.stringify(productsRes.data));
        sessionStorage.setItem(CATEGORY_CACHE_KEY, JSON.stringify(categoriesRes.data));
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const badge = cms?.badge || t('catalog.badge');
  const title = cms?.title || t('catalog.title');
  const subtitle = cms?.subtitle || t('catalog.subtitle');
  const searchPlaceholder = cms?.searchPlaceholder || 'Search figurines...';
  const filterAllText = cms?.filterAllText || 'All';
  const noResultsText = cms?.noResultsText || 'No figurines found';
  const clearFiltersText = cms?.clearFiltersText || 'Clear filters';

  const filteredProducts = (Array.isArray(products) ? products : []).filter((product) => {
    const matchesSearch = (product.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (product.shortDescription || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div data-testid="catalog-page" className="min-h-screen bg-[#0A0A0A] pt-24">
      <SEO page="catalog" lang={lang} path="/catalog" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-indigo-400 mb-4 block">
            {badge}
          </span>
          <h1 className="text-4xl lg:text-5xl font-['Outfit'] font-light tracking-tighter text-white mb-4">
            {title}
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto">{subtitle}</p>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row items-center gap-4 mb-12">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input type="text" placeholder={searchPlaceholder} value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} data-testid="catalog-search"
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors" />
          </div>
          <div className="hidden md:flex items-center space-x-2">
            <button onClick={() => setSelectedCategory('all')} data-testid="filter-all"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'all' ? 'bg-indigo-500 text-white' : 'bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10'
              }`}>{filterAllText}</button>
            {categories.map((category) => (
              <button key={category} onClick={() => setSelectedCategory(category)}
                data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category ? 'bg-indigo-500 text-white' : 'bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10'
                }`}>{category}</button>
            ))}
          </div>
          <button onClick={() => setShowFilters(!showFilters)} data-testid="mobile-filter-btn"
            className="md:hidden flex items-center space-x-2 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-zinc-400">
            <Filter className="w-5 h-5" /><span>Filters</span>
          </button>
        </div>

        {showFilters && (
          <div data-testid="mobile-filters" className="md:hidden mb-8 p-4 bg-zinc-900 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-medium">Categories</h3>
              <button onClick={() => setShowFilters(false)} className="text-zinc-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => { setSelectedCategory('all'); setShowFilters(false); }}
                className={`px-3 py-2 rounded-lg text-sm ${selectedCategory === 'all' ? 'bg-indigo-500 text-white' : 'bg-white/5 text-zinc-400'}`}>
                {filterAllText}
              </button>
              {categories.map((category) => (
                <button key={category} onClick={() => { setSelectedCategory(category); setShowFilters(false); }}
                  className={`px-3 py-2 rounded-lg text-sm ${selectedCategory === category ? 'bg-indigo-500 text-white' : 'bg-white/5 text-zinc-400'}`}>
                  {category}
                </button>
              ))}
            </div>
          </div>
        )}

        {!loading && (
          <p className="text-zinc-500 text-sm mb-6">
            Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'figurine' : 'figurines'}
            {selectedCategory !== 'all' && ` in ${selectedCategory}`}
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div data-testid="no-results" className="text-center py-24">
            <p className="text-zinc-400 text-lg mb-4">{noResultsText}</p>
            <button onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
              className="text-indigo-400 hover:text-indigo-300 font-medium">
              {clearFiltersText}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
